import { createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/layout";
import ogImageAsset from "@/assets/og-tb-logo-navy.png.asset.json";

const ogImage = `https://tablesandbridges.com${ogImageAsset.url}`;

const TITLE = "Accessibility Statement | Tables and Bridges";
const DESC =
  "Our commitment to making the Tables and Bridges website usable for law enforcement Community Engagement professionals of all abilities.";

export const Route = createFileRoute("/accessibility")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/accessibility" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: ogImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: ogImage },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/accessibility" }],
  }),
  component: AccessibilityPage,
});

function AccessibilityPage() {
  return (
    <div>
      <section className="bg-navy text-warm-white">
        <div className="container-editorial py-20 md:py-28">
          <p className="eyebrow text-gold mb-6">Fine print</p>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Accessibility Statement
          </h1>
          <p className="mt-6 text-warm-white/75 max-w-2xl">
            Tables and Bridges is committed to providing a website that is accessible to the
            widest possible audience, regardless of technology or ability.
          </p>
        </div>
      </section>

      <Section>
        <div className="container-editorial max-w-3xl space-y-10 text-soft-black/85 leading-relaxed">
          <Block title="Our commitment">
            We work to conform to the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA
            where reasonably achievable. Accessibility is treated as an ongoing practice, not a
            one-time project.
          </Block>
          <Block title="What we do">
            <ul className="list-disc pl-6 space-y-2">
              <li>Use semantic HTML and a single primary heading on each page.</li>
              <li>Maintain color contrast ratios that meet or exceed WCAG AA on core UI.</li>
              <li>Provide descriptive alt text for meaningful images and mark decorative images as such.</li>
              <li>Ensure interactive elements are reachable and operable with a keyboard.</li>
              <li>Design forms with visible labels and clear focus states.</li>
              <li>Support responsive layouts across common mobile, tablet, and desktop sizes.</li>
            </ul>
          </Block>
          <Block title="Known limitations">
            Some third-party embeds and legacy media may not fully meet our accessibility
            standards. We are working to identify and remediate these areas as we prepare the
            site for public launch.
          </Block>
          <Block title="Feedback">
            If you encounter an accessibility barrier on this site, please tell us. Your feedback
            helps us improve. Contact us through the{" "}
            <a href="/book-carlai" className="text-navy underline hover:text-gold">
              contact form
            </a>{" "}
            and include the page URL and a short description of the issue.
          </Block>
          <p className="text-sm text-soft-black/60">Last updated: 2026</p>
        </div>
      </Section>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="font-display text-2xl md:text-3xl text-navy mb-3">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
